/*globals jQuery:false */
/*globals gsap */
import { throttle } from "throttle-debounce";
//import gsap from 'gsap';
import IntersectionObserved from "./lib/intersection-observed.js";

const OPEN_ANIME_DURATION = 2;
const SELECT_ANIME_DURATION = 0.8;
const SELECTED_BG_SCALE_FACTOR = 12;

class GalleryGridComponent extends IntersectionObserved {
	constructor(el) {
		// console.log("GalleryGridComponent::constructor");
		super(el);

		const mobileTest = window.matchMedia("(max-width: 767px)");

		this.isMobile = mobileTest.matches;

		// if at mobile size on js init
		// add open class in case browser grows to desktop
		// add another class just for this special case
		// abort. no need to do any more js (no animations)
		if (this.isMobile && "ontouchstart" in window) {
			el.classList.add("gallery-grid-section--is-open");
			el.classList.add("gallery-grid-section--mobile-init");
			return;
		}

		// store references to our dom elements
		this.dom = {
			el,
			bounds: el.getBoundingClientRect(),
			items: el.querySelectorAll(".gallery-grid__element"),
			//ctas: el.querySelectorAll('.gallery-grid__cta > a'),
			backgrounds: el.querySelectorAll(".gallery-grid__background > div"),
			back: el.querySelector(".gallery-grid__back-cta"),
		};

		// console.log(this.dom);

		// store grid items and their updated positions
		this.items = [];

		// store temporary item clones
		this.clones = [];

		// the initial animation to the grid layout
		this.gridTl = gsap.timeline({ paused: true });

		// the animation to a single item from the grid
		this.selectTl = gsap.timeline({ paused: true });

		// the animation from a single item back to the grid
		this.deselectTl = gsap.timeline({ paused: true });

		this.lastSelectedIndex = null;

		// event listeners
		mobileTest.addListener(this.mobileTestUpdate.bind(this));

		this.addListeners();

		// start visibility listener
		this.initVisibilityCheck({
			//rootMargin: '-50%',
		});
	}

	// if we have inited our animation js but the browser goes mobile
	// set a flag so the animations dont run at mobile
	mobileTestUpdate(test) {
		this.isMobile = test.matches;

		// complete any active animation
		if (this.isMobile) {
			if (this.gridTl.isActive()) {
				this.gridTl.progress(1);
			} else if (this.selectTl.isActive()) {
				this.selectTl.progress(1);
			} else if (this.deselectTl.isActive()) {
				this.deselectTl.progress(1);
			}
		}
	}

	/**
	 * callback from intersection listener(s)
	 * start initial "to grid" anime
	 */
	onBecomeVisible() {
		// console.log("GalleryGridComponent::onBecomeVisible");

		if (this.hasBecomeVisible) return;

		// set inited flag
		this.hasBecomeVisible = true;

		// if our items' bg images aren't loaded
		// check them again on an interval
		// if not loaded after x number checks, run anime anyway
		if (this.bgsLoaded) {
			this.runToGridAnime();
		} else {
			this.bgChecks = 0;
			this.bgCheckID = setInterval(() => {
				// console.log("bgs loaded check", this.bgsLoaded, this.bgChecks);

				this.bgChecks++;

				if (this.allBgsLoaded || this.bgChecks > 5) {
					clearInterval(this.bgCheckID);
					this.runToGridAnime();
				}
			}, 200);
		}
	}

	/**
	 * To Grid Animation
	 *
	 */
	runToGridAnime() {
		// console.log("runToGridAnime");

		// jump to end if in mobile view
		if (this.isMobile) {
			this.el.classList.add("gallery-grid-section--is-open");
			for (let i = 0; i < this.dom.items.length; i++) {
				this.dom.items[i].style.opacity = 1;
			}
			this.addItemListeners();
			return;
		}

		this.setItemsCache();
		this.addGridClones();
		this.el.classList.add("gallery-grid-section--is-open");
		this.setItemsCache();

		this.setGridTweensCallbacks();

		this.gridTl.play();
	}

	// store item bounds and computed top vals
	// this function used by all three animations
	setItemsCache() {
		// console.log("setItemsCache");

		const containerBounds = this.el.getBoundingClientRect();

		let bounds;

		this.dom.bounds = containerBounds;

		this.items = [];

		[...this.dom.items].forEach((el) => {
			bounds = el.getBoundingClientRect();

			this.items.push({
				el,
				bounds,
				y: bounds.top - containerBounds.top,
			});
		});
	}

	// add destination props and complete callback to grid anime
	setGridTweensCallbacks() {
		let item, clone, left, top, width, height;

		this.gridTl.addLabel("start");

		for (let i = 0; i < this.items.length; i++) {
			item = this.items[i];
			clone = this.clones[i];

			left = item.bounds.left + "px";
			top = item.y + "px";
			width = item.bounds.width + "px";
			height = item.bounds.height + "px";

			this.gridTl.to(
				clone.el,
				{ duration: OPEN_ANIME_DURATION, left, top, width, height, ease: "sine.inOut" },
				"start",
			);
		}

		// anime onComplete
		// show the real grid items
		// remove our clone items
		// add click handler to grid items
		this.gridTl.eventCallback("onComplete", () => {
			// console.log("grid anime complete");
			for (let i = 0; i < this.items.length; i++) {
				this.items[i].el.style.opacity = 1;
				this.el.removeChild(this.clones[i].el);
			}
			this.addItemListeners();
		});
	}

	// place absolutely positioned clone items above grid items
	// hide grid items
	addGridClones() {
		// console.log("GalleryGrid::addClones");

		let item, clone;

		this.clones = [];

		for (let i = 0; i < this.items.length; i++) {
			item = this.items[i];

			clone = item.el.cloneNode(true);
			clone.classList.add("is-clone");
			clone.style.width = item.bounds.width + "px";
			clone.style.height = item.bounds.height + "px";

			clone.style.left = item.bounds.left + "px";
			clone.style.top = item.y + "px";

			if (i == 0) {
				clone.style.left = -item.bounds.width + "px";
			} else if (i == 1) {
				clone.style.left = this.dom.bounds.width + "px";
			} else if (i == 2) {
				clone.style.left = this.dom.bounds.width + this.items[2].bounds.width + "px";
			}

			item.el.style.opacity = 0;

			this.clones.push({
				el: clone,
				bounds: item.bounds,
			});
		}

		for (let i = this.clones.length - 1; i > -1; i--) {
			item.el.after(this.clones[i].el);
		}
	}

	/**
	 * Selected Item Animation
	 *
	 */
	runSelectAnime() {
		// console.log("runSelectAnime");

		const idx = this.lastSelectedIndex;

		// jump to end if in mobile view
		if (this.isMobile) {
			this.el.classList.remove("gallery-grid-section--is-open");
			this.el.classList.add("selected-idx-" + idx);
			if (this.dom.back) {
				this.dom.back.style.opacity = 1;
				this.dom.back.style.display = "flex";
			}
			return;
		}

		this.setItemsCache();
		this.addSelectClone();
		this.el.classList.remove("gallery-grid-section--is-open");
		this.el.classList.add("selected-idx-" + idx);
		this.setItemsCache();

		this.setSelectTweensCallbacks();

		this.selectTl.play();
	}

	// add destination props and complete callback to selected anime
	setSelectTweensCallbacks() {
		const idx = this.lastSelectedIndex;

		const item = this.items[idx];
		const clone = this.clones[0];
		const bg = clone.el.querySelector(".gallery-grid__background > div");
		const content = clone.el.querySelector(".gallery-grid__content");

		const left = item.bounds.left + "px";
		const top = item.y + "px";
		const width = item.bounds.width + "px";
		const height = item.bounds.height + "px";

		// console.log("selected bg", bg);

		content.style.display = "block";

		this.selectTl.addLabel("positions");
		this.selectTl.to(
			clone.el,
			{ duration: SELECT_ANIME_DURATION, left, top, width, height, ease: "sine.inOut" },
			"positions",
		);
		this.selectTl.to(
			bg,
			{
				duration: 0.6,
				delay: 0.2,
				left: "-" + SELECTED_BG_SCALE_FACTOR / 2 + "%",
				top: "-" + SELECTED_BG_SCALE_FACTOR / 2 + "%",
				width: 100 + SELECTED_BG_SCALE_FACTOR + "%",
				height: 100 + SELECTED_BG_SCALE_FACTOR + "%",
				ease: "sine.inOut",
			},
			"positions",
		);
		this.selectTl.addLabel("contents");
		this.selectTl.to(content, { duration: 0.8, opacity: 1, ease: "sine.inOut" }, "contents");
		if (this.dom.back) {
			this.selectTl.fromTo(
				this.dom.back,
				{ opacity: 0, display: "flex" },
				{ duration: 0.8, opacity: 1, ease: "sine.inOut" },
				"contents",
			);
		}

		// anime onComplete
		// show the real grid item
		// remove our clone item
		this.selectTl.eventCallback("onComplete", () => {
			// console.log("select anime complete");

			this.items[idx].el.style.opacity = 1;
			this.el.removeChild(clone.el);

			/*if (!this.isMobile) {
        // add bg image click handler
        let background = this.items[idx].el.querySelector('.gallery-grid__background');
        console.debug('background', background)
        background.addEventListener( 'click', this.handleBackButtonClick.bind(this) );
      }*/
		});
	}

	// place absolutely positioned clone item above selected grid items
	// hide selected grid item
	// add class on item to set background zoom (as clone will animate bg to zoomed state)
	// this function shared with deselected anime
	addSelectClone() {
		// console.log("addSelectClone");

		const idx = this.lastSelectedIndex;
		const item = this.items[idx];
		const clone = item.el.cloneNode(true);

		clone.classList.add("is-clone");
		clone.style.width = item.bounds.width + "px";
		clone.style.height = item.bounds.height + "px";

		clone.style.left = item.bounds.left + "px";
		clone.style.top = item.y + "px";

		clone.style.zIndex = 8;

		item.el.style.opacity = 0;
		item.el.classList.add("gallery-grid__element--bg-zoomed");

		this.clones = [
			{
				el: clone,
				bounds: item.bounds,
			},
		];

		this.items[3].el.after(clone);
	}

	/**
	 * Deselected Item Animation
	 *
	 */
	runDeselectAnime() {
		// console.log("runDeselectAnime");

		const idx = this.lastSelectedIndex;

		// jump to end if in mobile view
		if (this.isMobile) {
			this.el.classList.remove("selected-idx-" + idx);
			this.el.classList.add("gallery-grid-section--is-open");
			if (this.dom.back) {
				this.dom.back.style.opacity = 0;
				this.dom.back.style.display = "none";
			}
			this.lastSelectedIndex = null;
			return;
		}

		this.setItemsCache();
		this.addSelectClone();
		this.el.classList.remove("selected-idx-" + idx);
		this.el.classList.add("gallery-grid-section--is-open");
		this.setItemsCache();

		this.deselectTl.clear();
		this.setDeselectTweensCallbacks();
		this.deselectTl.play();
	}

	// add destination vals and complete callback to deselected anime
	setDeselectTweensCallbacks() {
		// console.log("setDeselectTweensCallbacks");

		const idx = this.lastSelectedIndex;

		const item = this.items[idx];
		const clone = this.clones[0];
		const bg = clone.el.querySelector(".gallery-grid__background > div");
		const content = clone.el.querySelector(".gallery-grid__content");

		const left = item.bounds.left + "px";
		const top = item.y + "px";
		const width = item.bounds.width + "px";
		const height = item.bounds.height + "px";

		// console.log("selected bg", bg);

		content.style.display = "block";

		this.deselectTl.addLabel("contents");
		if (this.dom.back) {
			this.deselectTl.fromTo(
				this.dom.back,
				{ opacity: 1 },
				{ duration: 0.8, opacity: 0, display: "none", ease: "sine.inOut" },
				"contents",
			);
		}
		this.deselectTl.fromTo(content, { opacity: 1 }, { duration: 0.8, opacity: 0, ease: "sine.inOut" }, "contents");
		this.deselectTl.addLabel("positions");
		this.deselectTl.to(
			bg,
			{
				duration: 0.6,
				delay: 0,
				left: 0,
				top: 0,
				width: "100%",
				height: "100%",
				ease: "sine.inOut",
			},
			"positions",
		);
		this.deselectTl.to(
			clone.el,
			{ duration: SELECT_ANIME_DURATION, delay: 0.2, left, top, width, height, ease: "sine.inOut" },
			"positions",
		);

		// anime onComplete
		// remove zoomed class from selected item (clone has de-zoomed its bg)
		// show the real grid item
		// remove our clone item
		// unset selected item prop
		this.deselectTl.eventCallback("onComplete", () => {
			// console.log("deselect anime complete");

			this.items[idx].el.classList.remove("gallery-grid__element--bg-zoomed");
			this.items[idx].el.style.opacity = 1;
			this.el.removeChild(clone.el);
			this.lastSelectedIndex = null;
		});
	}

	/**
	 * add throttled resize handler to pause and reset animations
	 * add handler for "back to grid" button; initialized deselect anime
	 */
	addListeners() {
		this.addBgLoadedListeners();

		this.throttledResize = throttle(250, this.handleResize.bind(this));

		window.addEventListener("resize", this.throttledResize);
		//window.addEventListener("orientationChange", this.throttledResize);

		if (this.dom.back) {
			this.dom.back.addEventListener("click", this.handleBackButtonClick.bind(this));
			/*this.dom.back.addEventListener('click', (evt) => {
        evt.stopImmediatePropagation();
        evt.preventDefault();
        
        console.log('back to grid btn click');
        
        if ( this.lastSelectedIndex !== null && !this.selectTl.isActive() && !this.deselectTl.isActive() ) {
          this.runDeselectAnime();
        }
        
        return false;
      });*/
		}
	}

	handleBackButtonClick(evt) {
		//evt.stopImmediatePropagation();
		//evt.preventDefault();

		// console.log("back to grid btn click");

		if (this.lastSelectedIndex !== null && !this.selectTl.isActive() && !this.deselectTl.isActive()) {
			this.runDeselectAnime();
		}

		return false;
	}

	// attempt to determine when our items' background images are loaded
	// ideally, the initial "to grid" anime won't run until all images are loaded
	addBgLoadedListeners() {
		let bg;

		const home = this;

		// flags
		this.allBgsLoaded = false;
		this.bgsToLoad = 0;
		this.bgsLoaded = 0;

		// loop the background elements and extract the image url
		// then add as src to new img el with load listener
		[...this.dom.backgrounds].forEach((el) => {
			bg = el.style.backgroundImage.slice(4, -1).replace(/['"]/g, "");

			if (bg) {
				this.bgsToLoad++;

				jQuery("<img/>")
					.attr("src", bg)
					.on("load", function () {
						jQuery(this).remove();

						home.bgsLoaded++;

						// console.log("bg " + home.bgsLoaded + " of " + home.bgsToLoad + " loaded");

						if (home.bgsLoaded >= home.bgsToLoad) {
							home.allBgsLoaded = true;
						}
					});
			}
		});

		if (this.bgsLoaded >= this.bgsToLoad) {
			this.allBgsLoaded = true;
		}
	}

	// check if an anime is active and reset its destination props before playing
	// TODO: make sure this is not being called concurrently, or handle if so
	handleResize() {
		// console.log("GalleryGrid::handleResize", this);

		if (this.gridTl.isActive()) {
			this.setItemsCache();
			this.gridTl.pause();
			this.gridTl.clear();
			this.setGridTweensCallbacks();
			this.gridTl.play();
		} else if (this.selectTl.isActive()) {
			this.setItemsCache();
			this.selectTl.pause();
			this.selectTl.clear();
			this.setSelectTweensCallbacks();
			this.selectTl.play();
		} else if (this.deselectTl.isActive()) {
			this.setItemsCache();
			this.deselectTl.pause();
			this.deselectTl.clear();
			this.setDeselectTweensCallbacks();
			this.deselectTl.play();
		}
	}

	/**
	 * add click handler to grid items
	 * only effective in grid view
	 * initializes select anime
	 */
	addItemListeners() {
		let item;

		for (let i = 0; i < this.dom.items.length; i++) {
			item = this.dom.items[i];

			item.addEventListener("click", (evt) => {
				// console.log("grid item " + i + " clicked");

				// set selected item property
				// start selected anime
				if (this.lastSelectedIndex === null) {
					this.lastSelectedIndex = i;
					this.runSelectAnime();
				} else {
					this.handleBackButtonClick(evt);
				}
			});
		}
	}
}

jQuery(document).ready(function () {
	let i;

	let gallerySections = document.querySelectorAll(".gallery-grid-section");

	for (i = 0; i < gallerySections.length; i++) {
		new GalleryGridComponent(gallerySections[i]);
	}
});
